'use client'

import { useState, useRef, useEffect } from 'react'
import { Search, Loader2 } from 'lucide-react'
import { LenisFadeIn } from '@/components/animations/LenisReveal'
import { FeatureCard } from '@/components/tools/FeatureCard'
import { ToolEntryHero } from '@/components/tools/ToolEntryHero'
import { Button } from '@/components/ui/button'
import ReadinessResults from './components/ReadinessResults'
import TeaserResult, {
  type TeaserReport,
} from '@/components/tools/TeaserResult'
import { useToolUsage } from '@/lib/use-tool-usage'
import { useToolAccessGate } from '@/components/tools/ToolAccessGate'
import { UsageLimitGate } from '@/components/tools/UsageLimitGate'

// Matches API response shape
interface CategoryResult {
  score: number
  maxScore: number
  color: string
  findings: { text: string; status: 'good' | 'warning' | 'bad' }[]
}

interface AiReadinessResult {
  url: string
  domain: string
  checkedAt: string
  overallScore: number
  grade: 'A' | 'B' | 'C' | 'D' | 'F'
  categories: {
    botDiscoverability: CategoryResult
    structuredData: CategoryResult
    llmCommunication: CategoryResult
    contentAccessibility: CategoryResult
    crossSignalReadiness: CategoryResult
  }
  crawlCheckAvailable: boolean
  llmsValidatorAvailable: boolean
  recommendations: {
    priority: 'high' | 'medium' | 'low'
    category: string
    message: string
  }[]
  aiSummary: {
    summary: string
    actions: string[]
    context: string
  } | null
}

const LOADING_STEPS = [
  'Starting AI readiness scan...',
  'Checking bot access and robots.txt...',
  'Analyzing structured data...',
  'Validating llms.txt...',
  'Analyzing entity definitions...',
  'Computing readiness scores...',
  'Generating AI recommendations...',
]

const SCORE_CATEGORIES = [
  { label: 'Bot Discoverability', color: '#008dcc', pts: '30 pts' },
  { label: 'Structured Data', color: '#8b5cf6', pts: '25 pts' },
  { label: 'LLM Communication', color: '#FDC304', pts: '25 pts' },
  { label: 'Content Accessibility', color: '#22c55e', pts: '15 pts' },
  { label: 'Cross-Signal Readiness', color: '#F90B8A', pts: '5 pts' },
]

function ResultsSkeleton() {
  const [stepIndex, setStepIndex] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      setStepIndex(prev => (prev < LOADING_STEPS.length - 1 ? prev + 1 : prev))
    }, 2500)
    return () => clearInterval(interval)
  }, [])

  const progress = Math.round(((stepIndex + 1) / LOADING_STEPS.length) * 100)

  return (
    <div className='space-y-6'>
      {/* Progress indicator */}
      <div className='bg-muted/50 rounded-2xl border border-line-strong p-6'>
        <div className='flex flex-col items-center gap-5 py-4'>
          {/* Animated score ring */}
          <div className='relative w-36 h-36'>
            <svg viewBox='0 0 120 120' className='w-full h-full -rotate-90'>
              <circle
                cx='60'
                cy='60'
                r='52'
                fill='none'
                stroke='var(--border)'
                strokeWidth='8'
                opacity={0.3}
              />
              <circle
                cx='60'
                cy='60'
                r='52'
                fill='none'
                stroke='var(--primary)'
                strokeWidth='8'
                strokeLinecap='round'
                strokeDasharray={`${2 * Math.PI * 52}`}
                strokeDashoffset={`${2 * Math.PI * 52 * (1 - progress / 100)}`}
                opacity={0.4}
                className='transition-all duration-1000 ease-out'
              />
            </svg>
            <div className='absolute inset-0 flex flex-col items-center justify-center'>
              <Loader2
                size={24}
                className='animate-spin text-muted-foreground mb-1'
              />
              <span className='text-xs font-medium text-muted-foreground'>
                {progress}%
              </span>
            </div>
          </div>

          {/* Step indicator */}
          <div className='text-center'>
            <p className='text-sm font-medium text-foreground mb-3'>
              {LOADING_STEPS[stepIndex]}
            </p>
            <div className='flex items-center justify-center gap-1.5'>
              {LOADING_STEPS.map((_, i) => (
                <div
                  key={i}
                  className='h-1.5 rounded-full transition-all duration-500'
                  style={{
                    width: i === stepIndex ? '24px' : '6px',
                    backgroundColor:
                      i <= stepIndex ? 'var(--primary)' : 'var(--border)',
                    opacity: i <= stepIndex ? 1 : 0.3,
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Score cards skeleton */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        {SCORE_CATEGORIES.map((cat, i) => {
          const stepMap = [1, 2, 3, 4, 5]
          const active = stepIndex >= stepMap[i]
          return (
            <div
              key={cat.label}
              className='bg-muted/50 rounded-2xl border border-line-strong p-5'
              style={{
                opacity: active ? 1 : 0.3,
                transition: 'opacity 0.5s ease',
              }}
            >
              <div className='flex items-center justify-between mb-3'>
                <div className='flex items-center gap-2'>
                  <span
                    className='h-2 w-2 rounded-full'
                    style={{ backgroundColor: cat.color }}
                  />
                  <span className='text-sm font-semibold text-foreground/70'>
                    {cat.label}
                  </span>
                </div>
                <span
                  className='text-[10px] font-bold rounded-full px-2 py-0.5'
                  style={{
                    backgroundColor: `${cat.color}15`,
                    color: cat.color,
                    opacity: active ? 1 : 0.3,
                  }}
                >
                  {cat.pts}
                </span>
              </div>
              <div className='skeleton h-2 w-full rounded-full mb-3' />
              <div className='space-y-1.5'>
                <div
                  className='skeleton h-3 rounded'
                  style={{ width: '70%' }}
                />
                <div
                  className='skeleton h-3 rounded'
                  style={{ width: '50%' }}
                />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default function AiReadinessScorePage() {
  const [result, setResult] = useState<AiReadinessResult | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const toolUsage = useToolUsage('ai-readiness')
  const gate = useToolAccessGate('ai-readiness', 'AI Readiness Score')
  const [stickyUrl, setStickyUrl] = useState('')
  const [showStickyBar, setShowStickyBar] = useState(false)
  const formRef = useRef<HTMLDivElement>(null)

  // IntersectionObserver: show sticky bar when form scrolls out of view
  useEffect(() => {
    if (!formRef.current) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowStickyBar(!entry.isIntersecting && result !== null)
      },
      { rootMargin: '-64px 0px 0px 0px' }
    )
    observer.observe(formRef.current)
    return () => observer.disconnect()
  }, [result])

  function validateUrl(value: string): boolean {
    if (!value.trim()) return false
    let test = value.trim()
    if (!test.startsWith('http://') && !test.startsWith('https://')) {
      test = `https://${test}`
    }
    try {
      const parsed = new URL(test)
      return parsed.hostname.includes('.')
    } catch {
      return false
    }
  }

  async function handleSubmit(inputUrl: string) {
    const trimmed = inputUrl.trim()
    if (!trimmed) {
      setError('Please enter a URL.')
      return
    }
    if (!validateUrl(trimmed)) {
      setError('Please enter a valid URL (e.g. example.com).')
      return
    }

    setStickyUrl(trimmed)

    if (!gate.ready) {
      gate.openGate(trimmed)
      return
    }

    setIsLoading(true)
    setError(null)
    setResult(null)

    try {
      const response = await toolUsage.fetchWithUsage(
        '/api/tools/ai-readiness',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: trimmed }),
        }
      )

      const data = await response.json()

      if (
        response.status === 403 &&
        (data.error === 'usage_limit' || data.error === 'gate_required')
      ) {
        gate.openGate(trimmed)
        return
      }

      if (response.status === 429 && data.error === 'domain_cap') {
        gate.showDomainCap(data)
        return
      }

      if (!response.ok) {
        throw new Error(data.message || 'Analysis failed. Please try again.')
      }

      setResult(data as AiReadinessResult)
      gate.markConsumed()

      // Scroll to results
      setTimeout(() => {
        document
          .getElementById('readiness-results')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 100)
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Something went wrong. Please try again.'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  function handleStickySubmit(e: React.FormEvent) {
    e.preventDefault()
    handleSubmit(stickyUrl)
  }

  return (
    <div className='animate-fade-in'>
      {gate.modal}
      {/* Sticky form bar */}
      {showStickyBar && (
        <div className='fixed top-16 left-0 right-0 z-30 border-b border-line-strong bg-background/95 backdrop-blur-sm'>
          <form
            onSubmit={handleStickySubmit}
            className='container mx-auto px-4 py-2.5 flex items-center gap-3 max-w-2xl'
          >
            <div className='relative flex-1'>
              <Search
                size={16}
                className='absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground'
              />
              <input
                type='text'
                aria-label='Website URL to analyze'
                value={stickyUrl}
                onChange={e => setStickyUrl(e.target.value)}
                placeholder='Enter a URL'
                className='w-full h-9 pl-9 pr-3 border border-border rounded-full bg-background text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary focus:border-primary transition-colors text-sm'
                disabled={isLoading}
              />
            </div>
            <Button
              type='submit'
              variant='default'
              size='sm'
              loading={isLoading}
              disabled={isLoading}
              className='h-9 px-5'
            >
              Analyze
            </Button>
          </form>
        </div>
      )}

      <div className='container mx-auto px-4 py-16 pb-24'>
        {/* Hero */}
        <LenisFadeIn>
          <div ref={formRef} className='text-center mb-10'>
            <ToolEntryHero
              title='How AI-Ready Is Your Website?'
              valueProp='One unified score for how AI search engines see your site: bot access, structured data, llms.txt, and content quality in a single actionable report.'
              trustLine='Free · 5 weighted categories · instant results'
              inputLabel='Website URL to analyze'
              placeholder='Enter a URL (e.g. example.com)'
              buttonLabel='Analyze'
              exampleValue='pixelmojo.io'
              isLoading={isLoading}
              onSubmit={handleSubmit}
            />
            <p className='text-sm text-muted-foreground max-w-2xl mx-auto mt-8'>
              This tool runs our AI Crawl Checker and llms.txt Validator
              simultaneously, then synthesizes the results into 5 weighted
              categories. You get a 0-100 readiness score, a pentagonal radar
              showing your strengths and gaps, and a prioritized action roadmap
              to improve your AI visibility.
            </p>

            {error && (
              <p className='text-sm text-[#F90B8A] mt-4 bg-[#F90B8A]/10 border border-[#F90B8A]/20 rounded-lg px-4 py-3 max-w-xl mx-auto'>
                {error}
              </p>
            )}
          </div>
        </LenisFadeIn>

        {/* Results */}
        <div id='readiness-results' className='max-w-4xl mx-auto'>
          {isLoading && <ResultsSkeleton />}
          {toolUsage.isGated && !isLoading && (
            <UsageLimitGate
              toolName='AI Readiness Score'
              used={toolUsage.used}
              limit={toolUsage.limit}
            />
          )}
          {result && !isLoading && !toolUsage.isGated && (
            <>
              {(result as unknown as { access?: { tier?: string } }).access
                ?.tier === 'teaser' ? (
                <TeaserResult
                  report={
                    (result as unknown as { report: TeaserReport }).report
                  }
                  toolName='AI Readiness Score'
                />
              ) : (
                <ReadinessResults result={result} />
              )}
            </>
          )}
        </div>

        {/* Pre-results info */}
        {!result && !isLoading && (
          <LenisFadeIn>
            <div className='max-w-3xl mx-auto mt-12'>
              <div className='bg-muted/50 rounded-2xl border border-line-strong p-8'>
                <h2 className='text-lg font-semibold text-foreground mb-4 text-center'>
                  What we analyze
                </h2>
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                  {[
                    {
                      title: 'Bot Discoverability',
                      desc: 'Can GPTBot, Claude, Perplexity, and search engines access your site? Do you have a deliberate bot policy?',
                      color: '#008dcc',
                    },
                    {
                      title: 'Structured Data',
                      desc: 'JSON-LD schema markup, Organization/Article types, navigation and rich schemas.',
                      color: '#8b5cf6',
                    },
                    {
                      title: 'LLM Communication',
                      desc: 'llms.txt quality, structure, entity definitions, link coverage, and use policy.',
                      color: '#FDC304',
                    },
                    {
                      title: 'Content Accessibility',
                      desc: 'Server-side rendering, title/description quality, and SSR framework detection.',
                      color: '#22c55e',
                    },
                    {
                      title: 'Cross-Signal Readiness',
                      desc: 'How well your signals work together: structured data + llms.txt + bot access + SSR.',
                      color: '#F90B8A',
                    },
                  ].map(item => (
                    <FeatureCard key={item.title} {...item} />
                  ))}
                </div>
              </div>
            </div>
          </LenisFadeIn>
        )}

        {/* Methodology */}
        <LenisFadeIn>
          <div className='max-w-4xl mx-auto mt-16'>
            <div className='bg-muted/50 rounded-2xl border border-line-strong p-8'>
              <h2 className='text-lg font-semibold text-foreground mb-2 text-center'>
                How We Score (100 Points Total)
              </h2>
              <p className='text-sm text-muted-foreground text-center mb-8 max-w-2xl mx-auto'>
                Your AI readiness score combines data from our Crawl Checker
                (bot access, structured data, content quality) and llms.txt
                Validator (structure, entities, links) into five weighted
                categories.
              </p>

              <div className='space-y-6'>
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                  {SCORE_CATEGORIES.map(item => (
                    <div
                      key={item.label}
                      className='bg-background border border-line-strong rounded-xl p-4'
                    >
                      <div className='flex items-center justify-between mb-2'>
                        <div className='flex items-center gap-2'>
                          <span
                            className='h-2 w-2 rounded-full'
                            style={{ backgroundColor: item.color }}
                          />
                          <span className='text-sm font-semibold text-foreground'>
                            {item.label}
                          </span>
                        </div>
                        <span
                          className='text-xs font-bold rounded-full px-2.5 py-0.5'
                          style={{
                            backgroundColor: `${item.color}15`,
                            color: item.color,
                          }}
                        >
                          {item.pts}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className='border-t border-line-strong pt-6'>
                  <h3 className='text-sm font-semibold text-foreground mb-3'>
                    Grade scale
                  </h3>
                  <div className='flex flex-wrap gap-3'>
                    {[
                      { grade: 'A', range: '85-100', color: '#22c55e' },
                      { grade: 'B', range: '70-84', color: '#008dcc' },
                      { grade: 'C', range: '55-69', color: '#FDC304' },
                      { grade: 'D', range: '40-54', color: '#f97316' },
                      { grade: 'F', range: '0-39', color: '#F90B8A' },
                    ].map(g => (
                      <span
                        key={g.grade}
                        className='inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5'
                        style={{
                          backgroundColor: `${g.color}15`,
                          color: g.color,
                        }}
                      >
                        {g.grade}: {g.range}
                      </span>
                    ))}
                  </div>
                </div>

                <div className='border-t border-line-strong pt-6'>
                  <h3 className='text-sm font-semibold text-foreground mb-3'>
                    Dive deeper with individual tools
                  </h3>
                  <div className='grid grid-cols-1 sm:grid-cols-2 gap-2'>
                    {[
                      {
                        title: 'Free AI Visibility Checker',
                        href: '/tools/free-ai-visibility-checker',
                        tag: 'Overview',
                        color: '#0ea5e9',
                      },
                      {
                        title: 'AI Crawl Checker',
                        href: '/tools/ai-crawl-checker',
                        tag: '14 Bots',
                        color: '#008dcc',
                      },
                      {
                        title: 'llms.txt Validator',
                        href: '/tools/llms-txt-validator',
                        tag: 'Deep Scan',
                        color: '#FDC304',
                      },
                      {
                        title: 'AI Citation Tracker',
                        href: '/tools/ai-citation-tracker',
                        tag: '4 Providers',
                        color: '#F90B8A',
                      },
                      {
                        title: 'Reddit Brand Monitor',
                        href: '/tools/reddit-brand-monitor',
                        tag: 'LLM Detection',
                        color: '#FDC304',
                      },
                    ].map(tool => (
                      <a
                        key={tool.href}
                        href={tool.href}
                        className='flex items-start gap-2.5 rounded-lg p-2.5 transition-colors hover:bg-muted'
                      >
                        <span
                          className='text-[10px] font-semibold uppercase rounded-full px-2 py-0.5 shrink-0 mt-0.5'
                          style={{
                            backgroundColor: `${tool.color}15`,
                            color: tool.color,
                          }}
                        >
                          {tool.tag}
                        </span>
                        <span className='text-xs text-foreground/80 leading-relaxed'>
                          {tool.title}
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </LenisFadeIn>
      </div>
    </div>
  )
}
